import {
  BillableCompanyList,
  CreateCompany,
  CreateInvoice,
  CreateJobPrice,
  CreateJobType,
  CreateOrPutStudentJobOffer,
  Invoice,
  InvoicesList,
  JobmarketPagination,
  JobPeriod,
  JobperiodList,
  JobPrice,
  JobType,
  StudentJobOffer,
  StudentJobOfferList,
  UpdateCompany,
  UpdateJobPrice,
  UpdateJobType
} from '@thro-types/jobmarket';

import { AxiosError, AxiosRequestConfig } from 'axios';
import { Gateway } from 'index';

import {
  Company,
  Course,
  CreateJobOffering,
  Faculty,
  JobOffering,
  JobofferingList
} from 'types/AppTypes';

export const getJobmarketCourses = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Course[]>('/jobmarket/v1/courses', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getJobmarketFaculties = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Faculty[]>('/jobmarket/v1/faculties', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getCompany = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Company>(`/jobmarket/v1/companies/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getUniversityCompany = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Company>('/jobmarket/v1/companies/university', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getCompanies = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<{ data: Company[]; pagination: JobmarketPagination }>(
      '/jobmarket/v1/companies',
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createCompany = async (data: CreateCompany, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Company>('/jobmarket/v1/companies', data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateCompany = async (
  id: number,
  data: UpdateCompany,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<Company>(`/jobmarket/v1/companies/${id}`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateCompanyState = async (
  id: number,
  data: { state: string; reason?: string },
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<Company>(
      `/jobmarket/v1/companies/${id}/state`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteCompany = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.delete<Company>(`/jobmarket/v1/companies/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createJobOffering = async (data: CreateJobOffering, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<JobOffering>('/jobmarket/v1/offers', data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateJobOffering = async (
  id: number,
  data: CreateJobOffering,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<JobOffering>(`/jobmarket/v1/offers/${id}`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const extendJobOffering = async (
  id: number,
  data: { jobPriceId: number; date?: Date | undefined },
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<JobOffering>(
      `/jobmarket/v1/offers/${id}/extend`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteJobOffering = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.delete<JobOffering>(`/jobmarket/v1/offers/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getJobOffering = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<JobOffering>(`/jobmarket/v1/offers/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getJobOfferings = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<JobofferingList>('/jobmarket/v1/offers', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getMyJobOfferings = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<JobofferingList>('/jobmarket/v1/offers/mine', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getBillableJobOfferings = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<JobofferingList>('/jobmarket/v1/offers/billable', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getJobTypes = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<JobType[]>('/jobmarket/v1/jobtypes', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getJobType = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<JobType>(`/jobmarket/v1/jobtypes/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getJobPrices = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<JobPrice[]>('/jobmarket/v1/prices', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getJobPrice = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<JobPrice>(`/jobmarket/v1/prices/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createJobPrice = async (data: CreateJobPrice, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<JobPrice>('/jobmarket/v1/prices', data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateJobPrice = async (
  id: number,
  data: UpdateJobPrice,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<JobPrice>(`/jobmarket/v1/prices/${id}`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createJobType = async (data: CreateJobType, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<JobType>('/jobmarket/v1/jobtypes', data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateJobType = async (
  id: number,
  data: UpdateJobType,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<JobType>(`/jobmarket/v1/jobtypes/${id}`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteJobPeriod = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.delete<JobPeriod>(`/jobmarket/v1/jobperiods/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deactivateJobPeriod = async (
  data: { jobPeriodIds: number[] },
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<JobPeriod>(
      `/jobmarket/v1/jobperiods/deactivate`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createJobmarketInvoice = async (data: CreateInvoice, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Invoice>(`/jobmarket/v1/invoices`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getJobmarketInvoices = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<InvoicesList>(`/jobmarket/v1/invoices`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getJobmarketInvoice = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Invoice>(`/jobmarket/v1/invoices/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getBillableJobPeriodsCount = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<number>(`/jobmarket/v1/invoices/billableCount`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getBillableCompanies = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<BillableCompanyList>(
      `/jobmarket/v1/invoices/billableCompanies`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getPeriodsForOffers = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<JobperiodList>(
      `/jobmarket/v1/invoices/periodsForOffers`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const exportCompanies = () => {
  return '/jobmarket/v1/companies/export' as const;
};

export const invoiceDownload = (id: number) => {
  return `/jobmarket/v1/invoices/${id}/download` as const;
};

export const attachmentDownload = (id: number) => {
  return `/jobmarket/v1/offers/${id}/attachement` as const;
};

export const createStudentJobOffering = async (
  data: CreateOrPutStudentJobOffer,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.post<StudentJobOffer>(
      '/jobmarket/v1/studentJobOffering',
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateStudentJobOffer = async (
  id: string,
  data: CreateOrPutStudentJobOffer,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<StudentJobOffer>(
      `/jobmarket/v1/studentJobOffering/${id}`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteStudentJobOffering = async (id: string, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.delete<StudentJobOffer>(
      `/jobmarket/v1/studentJobOffering/${id}`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getStudentJobOffering = async (id: string, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<StudentJobOffer>(
      `/jobmarket/v1/studentJobOffering/${id}`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getStudentJobOfferings = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<StudentJobOfferList>(
      '/jobmarket/v1/studentJobOffering',
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};
